import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  Building2, Plus, Edit2, Trash2, FileSpreadsheet, FileText,
  Users, DollarSign, Loader2, ChevronDown, Calendar, Search, UserPlus, X, Save
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-GT', {
    style: 'currency',
    currency: 'GTQ',
  }).format(amount || 0);
};

const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '-';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  const year = parts[0].slice(-2); // Only last 2 digits
  return `${parts[2]}/${parts[1]}/${year}`;
};

// IVA and ISR rates
const IVA_RATE = 0.12;
const ISR_RATE = 0.05;

// Calculate price breakdown with discount
const calculateBreakdown = (precioBruto, descuento = 0) => {
  const precioNeto = Math.max(0, precioBruto - descuento);
  const precioSinIva = precioNeto / (1 + IVA_RATE);
  const iva = precioNeto - precioSinIva;
  const isr = precioSinIva * ISR_RATE;
  const sinImpuestos = precioNeto - iva - isr;
  
  return {
    precio_bruto: precioBruto,
    descuento: descuento,
    precio_total: precioNeto,
    sin_impuestos: sinImpuestos,
    iva: iva,
    isr: isr
  };
};

const Societies = () => {
  const { token } = useAuth();
  const [societies, setSocieties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Society CRUD state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSociety, setEditingSociety] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    exam_types: '',
    members: [],
    is_active: true
  });
  
  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [societyToDelete, setSocietyToDelete] = useState(null);
  
  // Report view state
  const [selectedSociety, setSelectedSociety] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [startDate, setStartDate] = useState(format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  
  // Editable discounts state: { "patientIdx-examIdx": discountAmount }
  const [discounts, setDiscounts] = useState({});
  const [savingDiscounts, setSavingDiscounts] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    fetchSocieties();
  }, []);

  const fetchSocieties = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/societies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSocieties(response.data);
    } catch (error) {
      console.error('Error fetching societies:', error);
      toast.error('Error al cargar las sociedades');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingSociety(null);
    setFormData({
      name: '',
      description: '',
      exam_types: '',
      members: [],
      is_active: true
    });
    setIsModalOpen(true);
  };

  const openEditModal = (society) => {
    setEditingSociety(society);
    setFormData({
      name: society.name,
      description: society.description || '',
      exam_types: (society.exam_types || []).join(', '),
      members: society.members || [],
      is_active: society.is_active
    });
    setIsModalOpen(true);
  };

  const addMember = () => {
    setFormData({
      ...formData,
      members: [...formData.members, { name: '', percentage: 0 }]
    });
  };

  const updateMember = (index, field, value) => {
    const updatedMembers = [...formData.members];
    updatedMembers[index] = {
      ...updatedMembers[index],
      [field]: field === 'percentage' ? parseFloat(value) || 0 : value
    };
    setFormData({ ...formData, members: updatedMembers });
  };

  const removeMember = (index) => {
    setFormData({
      ...formData,
      members: formData.members.filter((_, i) => i !== index)
    });
  };

  const getTotalPercentage = () => {
    return formData.members.reduce((sum, m) => sum + (parseFloat(m.percentage) || 0), 0);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    // Validate members total percentage
    const totalPercentage = getTotalPercentage();
    if (formData.members.length > 0 && Math.abs(totalPercentage - 100) > 0.01) {
      toast.error(`El total de participación debe ser 100%. Actual: ${totalPercentage.toFixed(2)}%`);
      return;
    }

    // Validate member names
    const invalidMembers = formData.members.filter(m => !m.name.trim());
    if (invalidMembers.length > 0) {
      toast.error('Todos los miembros deben tener nombre');
      return;
    }

    setSaving(true);
    try {
      const examTypes = formData.exam_types
        .split(',')
        .map(t => t.trim())
        .filter(t => t.length > 0);

      const data = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        exam_types: examTypes,
        members: formData.members.map(m => ({
          name: m.name.trim(),
          percentage: parseFloat(m.percentage) || 0
        })),
        is_active: formData.is_active
      };

      if (editingSociety) {
        await axios.put(`${API_URL}/api/societies/${editingSociety.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Sociedad actualizada');
      } else {
        await axios.post(`${API_URL}/api/societies`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Sociedad creada');
      }

      setIsModalOpen(false);
      fetchSocieties();
    } catch (error) {
      console.error('Error saving society:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!societyToDelete) return;

    try {
      await axios.delete(`${API_URL}/api/societies/${societyToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sociedad eliminada');
      setDeleteDialogOpen(false);
      setSocietyToDelete(null);
      fetchSocieties();
    } catch (error) {
      console.error('Error deleting society:', error);
      toast.error('Error al eliminar');
    }
  };

  const fetchSocietyReport = async (society) => {
    setSelectedSociety(society);
    setReportLoading(true);
    setDiscounts({}); // Reset discounts when loading new report
    setHasUnsavedChanges(false);
    
    try {
      const response = await axios.get(
        `${API_URL}/api/societies/${society.id}/patients?start_date=${startDate}&end_date=${endDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReportData(response.data);
      
      // Pre-load saved discounts from the response
      const savedDiscounts = {};
      response.data.patients?.forEach((patient, pIdx) => {
        patient.examenes?.forEach((exam, eIdx) => {
          const key = `${pIdx}-${eIdx}`;
          
          // Check for existing saved discount first
          if (exam.society_discount > 0) {
            savedDiscounts[key] = exam.society_discount;
          } else {
            // Apply automatic discounts for Pentacam society
            const autoDiscount = calculatePentacamDiscount(society.name, exam, patient);
            if (autoDiscount > 0) {
              savedDiscounts[key] = autoDiscount;
            }
          }
        });
      });
      setDiscounts(savedDiscounts);
      
      // Mark as having changes if auto-discounts were applied
      const hasAutoDiscounts = Object.keys(savedDiscounts).length > 0 && 
        response.data.patients?.some((patient, pIdx) => 
          patient.examenes?.some((exam, eIdx) => {
            const key = `${pIdx}-${eIdx}`;
            return savedDiscounts[key] && !exam.society_discount;
          })
        );
      if (hasAutoDiscounts) {
        setHasUnsavedChanges(true);
        toast.info('Se aplicaron descuentos automáticos de Pentacam. Guarda para confirmar.');
      }
      
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Error al cargar el informe');
    } finally {
      setReportLoading(false);
    }
  };

  // Calculate automatic Pentacam discounts based on exam type and eye
  const calculatePentacamDiscount = (societyName, exam, patient) => {
    // Only apply to Pentacam society
    if (!societyName?.toLowerCase().includes('pentacam')) {
      return 0;
    }
    
    const tipoExamen = (exam.tipo_examen || '').toLowerCase();
    // Check if it's both eyes (AO = Ambos Ojos) or single eye
    const ojoEstudiar = (exam.ojo_estudiar || patient.ojo_estudiar || '').toUpperCase();
    const isBothEyes = ojoEstudiar === 'AO' || ojoEstudiar.includes('AMBOS');
    const eyeMultiplier = isBothEyes ? 2 : 1;
    
    // Discount rules for Pentacam:
    // 1. Q200.00 per eye for "paquete refractivo"
    // 2. Q450.00 per eye for "paquete catarata premium"
    
    if (tipoExamen.includes('paquete refractivo') || tipoExamen.includes('refractivo')) {
      return 200 * eyeMultiplier;
    }
    
    if (tipoExamen.includes('paquete catarata premium') || tipoExamen.includes('catarata premium')) {
      return 450 * eyeMultiplier;
    }
    
    return 0;
  };

  // Handle discount change for a specific exam
  const handleDiscountChange = (patientIdx, examIdx, value) => {
    const key = `${patientIdx}-${examIdx}`;
    const numValue = parseFloat(value) || 0;
    setDiscounts(prev => ({
      ...prev,
      [key]: numValue
    }));
    setHasUnsavedChanges(true);
  };

  // Save discounts to database
  const saveDiscounts = async () => {
    if (!reportData || !reportData.patients.length) return;
    
    setSavingDiscounts(true);
    try {
      // Build the array of patient/exam updates with discounts
      const updates = [];
      
      reportData.patients.forEach((patient, pIdx) => {
        patient.examenes.forEach((exam, eIdx) => {
          const key = `${pIdx}-${eIdx}`;
          const discount = discounts[key];
          
          // Only include if there's a discount value (even 0 to reset)
          if (discount !== undefined && discount !== null) {
            updates.push({
              patient_id: patient.id,
              exam_type: exam.tipo_examen,
              discount_amount: discount,
              society_id: selectedSociety.id
            });
          }
        });
      });
      
      if (updates.length === 0) {
        toast.info('No hay descuentos para guardar');
        setSavingDiscounts(false);
        return;
      }
      
      // Call the API to save discounts
      await axios.post(
        `${API_URL}/api/societies/${selectedSociety.id}/save-discounts`,
        { discounts: updates },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${updates.length} descuento(s) guardado(s) exitosamente`);
      setHasUnsavedChanges(false);
      
      // Refresh the report to show updated data
      fetchSocietyReport(selectedSociety);
      
    } catch (error) {
      console.error('Error saving discounts:', error);
      toast.error('Error al guardar los descuentos');
    } finally {
      setSavingDiscounts(false);
    }
  };

  // Get exam data with applied discount
  const getExamWithDiscount = (exam, patientIdx, examIdx) => {
    const key = `${patientIdx}-${examIdx}`;
    const discount = discounts[key] || 0;
    return calculateBreakdown(exam.precio_bruto, discount);
  };

  // Calculate totals with all discounts applied
  const getCalculatedTotals = () => {
    if (!reportData || !reportData.patients) {
      return {
        cantidad_pacientes: 0,
        total_bruto: 0,
        total_descuento: 0,
        total_facturado: 0,
        total_sin_impuestos: 0,
        total_iva: 0,
        total_isr: 0
      };
    }

    let totals = {
      cantidad_pacientes: reportData.totals.cantidad_pacientes,
      total_bruto: 0,
      total_descuento: 0,
      total_facturado: 0,
      total_sin_impuestos: 0,
      total_iva: 0,
      total_isr: 0
    };

    reportData.patients.forEach((patient, pIdx) => {
      patient.examenes.forEach((exam, eIdx) => {
        const calculated = getExamWithDiscount(exam, pIdx, eIdx);
        totals.total_bruto += calculated.precio_bruto;
        totals.total_descuento += calculated.descuento;
        totals.total_facturado += calculated.precio_total;
        totals.total_sin_impuestos += calculated.sin_impuestos;
        totals.total_iva += calculated.iva;
        totals.total_isr += calculated.isr;
      });
    });

    return totals;
  };

  const exportToExcel = () => {
    if (!reportData || !reportData.patients.length) {
      toast.error('No hay datos para exportar');
      return;
    }

    const calculatedTotals = getCalculatedTotals();
    const rows = [];
    
    // Header info
    rows.push([`INFORME SOCIEDAD: ${selectedSociety.name}`]);
    rows.push([`Período: ${formatDateDisplay(startDate)} al ${formatDateDisplay(endDate)}`]);
    rows.push([]);
    rows.push(['RESUMEN']);
    rows.push(['Total Pacientes:', calculatedTotals.cantidad_pacientes]);
    rows.push(['Precio Bruto:', formatCurrency(calculatedTotals.total_bruto)]);
    rows.push(['Total Descuento:', formatCurrency(calculatedTotals.total_descuento)]);
    rows.push(['Total Neto:', formatCurrency(calculatedTotals.total_facturado)]);
    rows.push(['Sin Impuestos:', formatCurrency(calculatedTotals.total_sin_impuestos)]);
    rows.push(['Total IVA:', formatCurrency(calculatedTotals.total_iva)]);
    rows.push(['Total ISR:', formatCurrency(calculatedTotals.total_isr)]);
    rows.push([]);
    rows.push(['DETALLE DE PACIENTES']);
    rows.push(['Fecha', 'Paciente', 'Médico Referente', 'Tipo de Estudio', 'Es Paquete', 'Precio Bruto', 'Descuento', 'Precio Neto', 'Sin Impuestos', 'IVA', 'ISR']);

    reportData.patients.forEach((patient, pIdx) => {
      patient.examenes.forEach((exam, eIdx) => {
        const calculated = getExamWithDiscount(exam, pIdx, eIdx);
        rows.push([
          formatDateDisplay(patient.fecha),
          patient.nombre,
          patient.medico_referente || '-',
          exam.tipo_examen,
          exam.es_paquete ? 'Sí' : 'No',
          formatCurrency(calculated.precio_bruto),
          formatCurrency(calculated.descuento),
          formatCurrency(calculated.precio_total),
          formatCurrency(calculated.sin_impuestos),
          formatCurrency(calculated.iva),
          formatCurrency(calculated.isr)
        ]);
      });
    });

    // Add totals row
    rows.push([]);
    rows.push(['', '', '', 'TOTALES:', '',
      formatCurrency(calculatedTotals.total_bruto),
      formatCurrency(calculatedTotals.total_descuento),
      formatCurrency(calculatedTotals.total_facturado),
      formatCurrency(calculatedTotals.total_sin_impuestos),
      formatCurrency(calculatedTotals.total_iva),
      formatCurrency(calculatedTotals.total_isr)
    ]);

    // Add members distribution if society has members
    if (selectedSociety.members && selectedSociety.members.length > 0) {
      rows.push([]);
      rows.push([]);
      rows.push(['DISTRIBUCIÓN POR SOCIO (basado en Sin Impuestos)']);
      rows.push(['Socio', 'Porcentaje', 'Monto']);
      
      const baseAmount = calculatedTotals.total_sin_impuestos;
      selectedSociety.members.forEach(member => {
        const memberAmount = (baseAmount * member.percentage) / 100;
        rows.push([
          member.name,
          `${member.percentage}%`,
          formatCurrency(memberAmount)
        ]);
      });
      
      rows.push(['TOTAL', '100%', formatCurrency(baseAmount)]);
    }

    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Informe');
    XLSX.writeFile(wb, `Sociedad_${selectedSociety.name}_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
    toast.success('Excel exportado');
  };

  const exportToPDF = () => {
    if (!reportData || !reportData.patients.length) {
      toast.error('No hay datos para exportar');
      return;
    }

    const calculatedTotals = getCalculatedTotals();
    const doc = new jsPDF('landscape');
    
    // Title
    doc.setFontSize(16);
    doc.text(`Informe Sociedad: ${selectedSociety.name}`, 14, 20);
    
    doc.setFontSize(10);
    doc.text(`Período: ${formatDateDisplay(startDate)} al ${formatDateDisplay(endDate)}`, 14, 28);
    
    // Summary
    doc.setFontSize(12);
    doc.text('Resumen', 14, 40);
    doc.setFontSize(10);
    doc.text(`Total Pacientes: ${calculatedTotals.cantidad_pacientes}`, 14, 48);
    doc.text(`Precio Bruto: ${formatCurrency(calculatedTotals.total_bruto)}`, 14, 54);
    doc.text(`Total Descuento: ${formatCurrency(calculatedTotals.total_descuento)}`, 14, 60);
    doc.text(`Total Neto: ${formatCurrency(calculatedTotals.total_facturado)}`, 100, 48);
    doc.text(`Sin Impuestos: ${formatCurrency(calculatedTotals.total_sin_impuestos)}`, 100, 54);
    doc.text(`Total IVA: ${formatCurrency(calculatedTotals.total_iva)}`, 180, 48);
    doc.text(`Total ISR: ${formatCurrency(calculatedTotals.total_isr)}`, 180, 54);

    // Detail table
    const tableData = [];
    reportData.patients.forEach((patient, pIdx) => {
      patient.examenes.forEach((exam, eIdx) => {
        const calculated = getExamWithDiscount(exam, pIdx, eIdx);
        tableData.push([
          formatDateDisplay(patient.fecha),
          patient.nombre,
          patient.medico_referente || '-',
          exam.tipo_examen,
          exam.es_paquete ? 'Sí' : 'No',
          formatCurrency(calculated.precio_bruto),
          formatCurrency(calculated.descuento),
          formatCurrency(calculated.precio_total),
          formatCurrency(calculated.sin_impuestos),
          formatCurrency(calculated.iva),
          formatCurrency(calculated.isr)
        ]);
      });
    });

    autoTable(doc, {
      startY: 70,
      head: [['Fecha', 'Paciente', 'Médico Ref.', 'Estudio', 'Paq.', 'Bruto', 'Desc.', 'Neto', 'Sin Imp.', 'IVA', 'ISR']],
      body: tableData,
      foot: [['', '', '', 'TOTALES:', '',
        formatCurrency(calculatedTotals.total_bruto),
        formatCurrency(calculatedTotals.total_descuento),
        formatCurrency(calculatedTotals.total_facturado),
        formatCurrency(calculatedTotals.total_sin_impuestos),
        formatCurrency(calculatedTotals.total_iva),
        formatCurrency(calculatedTotals.total_isr)
      ]],
      styles: { fontSize: 7 },
      headStyles: { fillColor: [26, 81, 146] }
    });

    // Add members distribution if society has members
    if (selectedSociety.members && selectedSociety.members.length > 0) {
      const finalY = doc.lastAutoTable.finalY || 150;
      
      doc.setFontSize(12);
      doc.text('Distribución por Socio (basado en Sin Impuestos)', 14, finalY + 15);
      
      const memberData = [];
      const baseAmount = calculatedTotals.total_sin_impuestos;
      
      selectedSociety.members.forEach(member => {
        const memberAmount = (baseAmount * member.percentage) / 100;
        memberData.push([
          member.name,
          `${member.percentage}%`,
          formatCurrency(memberAmount)
        ]);
      });
      
      autoTable(doc, {
        startY: finalY + 20,
        head: [['Socio', 'Porcentaje', 'Monto']],
        body: memberData,
        foot: [['TOTAL', '100%', formatCurrency(baseAmount)]],
        styles: { fontSize: 9 },
        headStyles: { fillColor: [6, 148, 162] },
        footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' }
      });
    }

    doc.save(`Sociedad_${selectedSociety.name}_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    toast.success('PDF exportado');
  };

  return (
    <div className="p-6 space-y-6" data-testid="societies-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <Building2 className="w-7 h-7 text-[#1a5192]" />
            Sociedades
          </h1>
          <p className="text-slate-500 mt-1">Gestión de sociedades de inversionistas</p>
        </div>
        <Button onClick={openCreateModal} className="bg-[#1a5192] hover:bg-[#15407a]">
          <Plus className="w-4 h-4 mr-2" />
          Nueva Sociedad
        </Button>
      </div>

      {/* Societies Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : societies.length === 0 ? (
          <div className="col-span-full text-center py-12 text-slate-400">
            No hay sociedades registradas
          </div>
        ) : (
          societies.map((society) => (
            <Card key={society.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-[#1a5192]" />
                      {society.name}
                    </CardTitle>
                    {society.description && (
                      <p className="text-sm text-slate-500 mt-1">{society.description}</p>
                    )}
                  </div>
                  <Badge className={society.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                    {society.is_active ? 'Activa' : 'Inactiva'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-xs text-slate-500 mb-1">Tipos de Examen:</p>
                  <div className="flex flex-wrap gap-1">
                    {(society.exam_types || []).map((exam, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs border-slate-300">
                        {exam}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Members section */}
                {society.members && society.members.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Miembros:</p>
                    <div className="space-y-1">
                      {society.members.map((member, idx) => (
                        <div key={idx} className="flex justify-between items-center text-sm">
                          <span className="text-slate-700">{member.name}</span>
                          <Badge className="bg-blue-100 text-blue-700 text-xs">
                            {member.percentage}%
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="flex items-center gap-2 pt-2 border-t">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 text-[#1a5192] border-[#1a5192] hover:bg-[#1a5192]/10"
                    onClick={() => fetchSocietyReport(society)}
                    data-testid={`society-report-btn-${society.id}`}
                  >
                    <FileText className="w-4 h-4 mr-1" />
                    Ver Informe
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openEditModal(society)}
                    className="text-slate-500 hover:text-[#1a5192]"
                    data-testid={`society-edit-btn-${society.id}`}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { setSocietyToDelete(society); setDeleteDialogOpen(true); }}
                    className="text-slate-500 hover:text-red-500"
                    data-testid={`society-delete-btn-${society.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Report Section */}
      {selectedSociety && (
        <Card className="mt-6">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#1a5192]" />
                Informe: {selectedSociety.name}
              </CardTitle>
              
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-slate-600">Desde:</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-40"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-slate-600">Hasta:</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-40"
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={() => fetchSocietyReport(selectedSociety)}
                  disabled={reportLoading}
                >
                  {reportLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  <span className="ml-2">Buscar</span>
                </Button>
                
                {reportData && reportData.patients.length > 0 && (
                  <>
                    {/* Save Button */}
                    {hasUnsavedChanges && (
                      <Button 
                        onClick={saveDiscounts}
                        disabled={savingDiscounts}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        {savingDiscounts ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Guardando...</>
                        ) : (
                          <><Save className="w-4 h-4 mr-2" />Guardar Descuentos</>
                        )}
                      </Button>
                    )}
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="text-emerald-600 border-emerald-300">
                          <FileSpreadsheet className="w-4 h-4 mr-2" />
                          Exportar
                          <ChevronDown className="w-4 h-4 ml-2" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem onClick={exportToExcel}>
                          <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" />
                          Excel
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={exportToPDF}>
                          <FileText className="w-4 h-4 mr-2 text-red-600" />
                          PDF
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {reportLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : reportData ? (
              <div className="space-y-6">
                {/* Summary Cards - using calculated totals */}
                {(() => {
                  const totals = getCalculatedTotals();
                  return (
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                      <Card className="bg-blue-50 border-blue-200">
                        <CardContent className="p-3 text-center">
                          <Users className="w-5 h-5 mx-auto text-blue-600 mb-1" />
                          <p className="text-xl font-bold text-blue-700">{totals.cantidad_pacientes}</p>
                          <p className="text-xs text-blue-600">Pacientes</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-slate-50 border-slate-200">
                        <CardContent className="p-3 text-center">
                          <p className="text-lg font-bold text-slate-700">{formatCurrency(totals.total_bruto)}</p>
                          <p className="text-xs text-slate-600">Precio Bruto</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-emerald-50 border-emerald-200">
                        <CardContent className="p-3 text-center">
                          <DollarSign className="w-5 h-5 mx-auto text-emerald-600 mb-1" />
                          <p className="text-lg font-bold text-emerald-700">{formatCurrency(totals.total_facturado)}</p>
                          <p className="text-xs text-emerald-600">Total Neto</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-slate-50 border-slate-200">
                        <CardContent className="p-3 text-center">
                          <p className="text-lg font-bold text-slate-700">{formatCurrency(totals.total_sin_impuestos)}</p>
                          <p className="text-xs text-slate-600">Sin Impuestos</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-amber-50 border-amber-200">
                        <CardContent className="p-3 text-center">
                          <p className="text-lg font-bold text-amber-700">{formatCurrency(totals.total_iva)}</p>
                          <p className="text-xs text-amber-600">IVA</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-purple-50 border-purple-200">
                        <CardContent className="p-3 text-center">
                          <p className="text-lg font-bold text-purple-700">{formatCurrency(totals.total_isr)}</p>
                          <p className="text-xs text-purple-600">ISR</p>
                        </CardContent>
                      </Card>
                    </div>
                  );
                })()}

                {/* Members Distribution Cards */}
                {selectedSociety?.members && selectedSociety.members.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                      <Users className="w-4 h-4 text-[#1a5192]" />
                      Distribución por Socio (basado en Sin Impuestos)
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                      {(() => {
                        const totals = getCalculatedTotals();
                        const baseAmount = totals.total_sin_impuestos;
                        return selectedSociety.members.map((member, idx) => {
                          const memberAmount = (baseAmount * member.percentage) / 100;
                          return (
                            <Card key={idx} className="bg-gradient-to-br from-cyan-50 to-teal-50 border-cyan-200">
                              <CardContent className="p-3 text-center">
                                <p className="text-xs text-cyan-600 font-medium truncate mb-1" title={member.name}>
                                  {member.name}
                                </p>
                                <p className="text-lg font-bold text-cyan-700">
                                  {formatCurrency(memberAmount)}
                                </p>
                                <Badge className="bg-cyan-100 text-cyan-700 text-xs mt-1">
                                  {member.percentage}%
                                </Badge>
                              </CardContent>
                            </Card>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}

                {/* Detail Table */}
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-100">
                        <TableHead className="font-semibold">Fecha</TableHead>
                        <TableHead className="font-semibold">Paciente</TableHead>
                        <TableHead className="font-semibold">Médico Referente</TableHead>
                        <TableHead className="font-semibold">Tipo de Estudio</TableHead>
                        <TableHead className="font-semibold text-right">Precio Bruto</TableHead>
                        <TableHead className="font-semibold text-center w-28">Descuento (Q)</TableHead>
                        <TableHead className="font-semibold text-right">Precio Neto</TableHead>
                        <TableHead className="font-semibold text-right">Sin Impuestos</TableHead>
                        <TableHead className="font-semibold text-right">IVA</TableHead>
                        <TableHead className="font-semibold text-right">ISR</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData.patients.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} className="text-center py-8 text-slate-400">
                            No hay pacientes en este período
                          </TableCell>
                        </TableRow>
                      ) : (
                        reportData.patients.map((patient, pIdx) => (
                          patient.examenes.map((exam, eIdx) => {
                            const calculated = getExamWithDiscount(exam, pIdx, eIdx);
                            const discountKey = `${pIdx}-${eIdx}`;
                            return (
                              <TableRow key={discountKey} className="hover:bg-slate-50">
                                <TableCell className="font-mono text-sm">{formatDateDisplay(patient.fecha)}</TableCell>
                                <TableCell className="font-medium">{patient.nombre}</TableCell>
                                <TableCell>{patient.medico_referente || '-'}</TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline" className="border-[#1a5192] text-[#1a5192]">
                                      {exam.tipo_examen}
                                    </Badge>
                                    {exam.es_paquete && (
                                      <Badge className="bg-purple-100 text-purple-700 text-xs">Paquete</Badge>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="text-right">{formatCurrency(exam.precio_bruto)}</TableCell>
                                <TableCell className="text-center">
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={discounts[discountKey] || ''}
                                    onChange={(e) => handleDiscountChange(pIdx, eIdx, e.target.value)}
                                    placeholder="0.00"
                                    className="w-24 h-8 text-right text-sm mx-auto"
                                  />
                                </TableCell>
                                <TableCell className="text-right font-semibold">{formatCurrency(calculated.precio_total)}</TableCell>
                                <TableCell className="text-right">{formatCurrency(calculated.sin_impuestos)}</TableCell>
                                <TableCell className="text-right text-amber-600">{formatCurrency(calculated.iva)}</TableCell>
                                <TableCell className="text-right text-purple-600">{formatCurrency(calculated.isr)}</TableCell>
                              </TableRow>
                            );
                          })
                        ))
                      )}
                    </TableBody>
                    {reportData.patients.length > 0 && (
                      <tfoot>
                        {(() => {
                          const totals = getCalculatedTotals();
                          return (
                            <TableRow className="bg-slate-100 font-semibold">
                              <TableCell colSpan={4} className="text-right">TOTALES:</TableCell>
                              <TableCell className="text-right">{formatCurrency(totals.total_bruto)}</TableCell>
                              <TableCell className="text-center text-red-600">
                                {totals.total_descuento > 0 ? `-${formatCurrency(totals.total_descuento)}` : '-'}
                              </TableCell>
                              <TableCell className="text-right">{formatCurrency(totals.total_facturado)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(totals.total_sin_impuestos)}</TableCell>
                              <TableCell className="text-right text-amber-600">{formatCurrency(totals.total_iva)}</TableCell>
                              <TableCell className="text-right text-purple-600">{formatCurrency(totals.total_isr)}</TableCell>
                            </TableRow>
                          );
                        })()}
                      </tfoot>
                    )}
                  </Table>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                Seleccione un rango de fechas y haga clic en Buscar
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#1a5192]" />
              {editingSociety ? 'Editar Sociedad' : 'Nueva Sociedad'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="society_name">Nombre *</Label>
              <Input
                id="society_name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ej: USG, Funes"
                className="bg-slate-50"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="society_description">Descripción</Label>
              <Textarea
                id="society_description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descripción de la sociedad..."
                className="bg-slate-50 resize-none"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="society_exams">Tipos de Examen</Label>
              <Textarea
                id="society_exams"
                value={formData.exam_types}
                onChange={(e) => setFormData({ ...formData, exam_types: e.target.value })}
                placeholder="Separados por coma: UBM, USG Modo B, etc."
                className="bg-slate-50 resize-none"
                rows={3}
              />
              <p className="text-xs text-slate-500">Ingrese los tipos de examen separados por coma</p>
            </div>

            {/* Members Section */}
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#1a5192]" />
                  Miembros de la Sociedad
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addMember}
                  className="text-[#1a5192] border-[#1a5192]"
                >
                  <UserPlus className="w-4 h-4 mr-1" />
                  Agregar
                </Button>
              </div>

              {formData.members.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4 bg-slate-50 rounded-lg">
                  No hay miembros agregados
                </p>
              ) : (
                <div className="space-y-2">
                  {formData.members.map((member, index) => (
                    <div key={index} className="flex items-center gap-2 bg-slate-50 p-2 rounded-lg">
                      <Input
                        value={member.name}
                        onChange={(e) => updateMember(index, 'name', e.target.value)}
                        placeholder="Nombre del miembro"
                        className="flex-1 bg-white"
                      />
                      <div className="flex items-center gap-1 w-28">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={member.percentage}
                          onChange={(e) => updateMember(index, 'percentage', e.target.value)}
                          className="bg-white text-right"
                        />
                        <span className="text-slate-600">%</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeMember(index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 h-8 w-8"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  
                  {/* Total percentage indicator */}
                  <div className={`flex justify-end items-center gap-2 pt-2 border-t ${
                    Math.abs(getTotalPercentage() - 100) < 0.01 
                      ? 'text-emerald-600' 
                      : 'text-red-600'
                  }`}>
                    <span className="text-sm font-medium">Total:</span>
                    <Badge className={
                      Math.abs(getTotalPercentage() - 100) < 0.01
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-red-100 text-red-700'
                    }>
                      {getTotalPercentage().toFixed(2)}%
                    </Badge>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="society_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded border-slate-300"
              />
              <Label htmlFor="society_active" className="cursor-pointer">Sociedad activa</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#1a5192] hover:bg-[#15407a]">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingSociety ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar sociedad?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará la sociedad &quot;{societyToDelete?.name}&quot; permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Societies;
